import {useIntl} from 'react-intl';

/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {AuthHelpers, useAuthHelper} from '@salesforce/commerce-sdk-react';
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast';
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils';

import {toSfccLocale} from '@amblique/king-living/app/utils/utils';

/**
 * Hook that provides commerce-react-sdk helpers for the reset password flow.
 * Appends locale (and site id) to the callback URI query so SSR can build localized magic links
 * without changing the SLAS callback path.
 */

export const usePasswordReset = () => {
  const showToast = useToast();
  const {formatMessage, locale} = useIntl();
  const appOrigin = useAppOrigin();
  const {site} = useMultiSite();
  const config = getConfig();
  const resetPasswordCallback =
    config.app.login?.resetPassword?.callbackURI || '/reset-password-callback';
  const baseCallbackURI = isAbsoluteURL(resetPasswordCallback)
    ? resetPasswordCallback
    : `${appOrigin}${resetPasswordCallback}`;
  const localeParam = toSfccLocale(locale);
  let callbackURI = baseCallbackURI;
  const appendQuery = (key, value) => {
    const sep = callbackURI.includes('?') ? '&' : '?';
    callbackURI += `${sep}${key}=${encodeURIComponent(value)}`;
  };
  if (localeParam) {
    appendQuery('locale', localeParam);
  }
  if (site?.id) {
    appendQuery('site', site.id);
  }

  const getPasswordResetTokenMutation = useAuthHelper(AuthHelpers.GetPasswordResetToken);
  const resetPasswordMutation = useAuthHelper(AuthHelpers.ResetPassword);


  const getPasswordResetToken = async (email) => {
    await getPasswordResetTokenMutation.mutateAsync({
      user_id: email,
      callback_uri: callbackURI
    });
  };

  const resetPassword = async ({email, token, newPassword}) => {
    await resetPasswordMutation.mutateAsync(
      {user_id: email, pwd_action_token: token, new_password: newPassword},
      {
        onSuccess: () => {
          showToast({
            title: formatMessage({
              defaultMessage: 'Password Reset Success',
              id: 'password_reset_success.toast',
            }),
            status: 'success',
            position: 'bottom-right',
          });
        },
      }
    );
  };

  return {getPasswordResetToken, resetPassword};
};
